import { useMemo, useState } from 'react'
import { keepPreviousData } from '@tanstack/react-query'
import {
  addDays,
  addYears,
  differenceInCalendarDays,
  differenceInYears,
  endOfMonth,
  format,
  isValid,
  parseISO,
  setYear,
  startOfDay,
  startOfMonth,
} from 'date-fns'
import { AlertCircle } from 'lucide-react'

import { EnhancedDataTable, type ColumnConfig } from '@/components/tables/enhanced-data-table'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { ApplicationListSkeleton } from '@/features/admin/applications/components/ApplicationListSkeleton'
import {
  formatFullName,
  mapApplicationsResponse,
} from '@/features/admin/applications/utils'
import { useListBirthdays } from '@/utils/api/membership-service'
import type { AdminApplication } from '@/features/admin/applications/types'

type DateRange = {
  from: string
  to: string
}

type BirthdayRow = {
  id: number
  name: string
  email: string
  phone: string
  membershipNumber?: string | null
  category: string
  dateOfBirth?: string | null
  dateOfBirthLabel: string
  nextBirthdayLabel: string
  nextBirthdaySortable: number | null
  daysUntil: number | null
  turningAge: number | null
}

function buildDefaultRange(): DateRange {
  const today = startOfDay(new Date())
  return {
    from: format(today, 'yyyy-MM-dd'),
    to: format(addDays(today, 30), 'yyyy-MM-dd'),
  }
}

function buildQuickRanges(): Array<{ label: string; range: DateRange }> {
  const today = startOfDay(new Date())
  const startMonth = startOfMonth(today)

  return [
    { label: 'Today', range: { from: format(today, 'yyyy-MM-dd'), to: format(today, 'yyyy-MM-dd') } },
    { label: 'Next 7 days', range: { from: format(today, 'yyyy-MM-dd'), to: format(addDays(today, 6), 'yyyy-MM-dd') } },
    { label: 'Next 30 days', range: { from: format(today, 'yyyy-MM-dd'), to: format(addDays(today, 29), 'yyyy-MM-dd') } },
    { label: 'This month', range: { from: format(startMonth, 'yyyy-MM-dd'), to: format(endOfMonth(today), 'yyyy-MM-dd') } },
  ]
}

function parseDateInput(value?: string | null): Date | null {
  if (!value) return null
  try {
    const parsed = parseISO(value)
    return isValid(parsed) ? parsed : null
  } catch {
    return null
  }
}

function validateRange(range: DateRange): string | null {
  const fromDate = parseDateInput(range.from)
  const toDate = parseDateInput(range.to)
  if (!fromDate || !toDate) return 'Select both start and end dates.'
  if (fromDate > toDate) return 'End date must be on or after the start date.'
  return null
}

function formatRangeLabel(range: DateRange): string {
  const fromDate = parseDateInput(range.from)
  const toDate = parseDateInput(range.to)
  if (!fromDate || !toDate) return 'No date range selected.'
  if (range.from === range.to) return `Birthdays on ${format(fromDate, 'PPP')}`
  return `Birthdays from ${format(fromDate, 'PPP')} to ${format(toDate, 'PPP')}`
}

function computeBirthdayMeta(dateOfBirth: string | null): {
  dateOfBirthLabel: string
  nextBirthdayLabel: string
  nextBirthdaySortable: number | null
  daysUntil: number | null
  turningAge: number | null
} {
  const parsed = parseDateInput(dateOfBirth)
  if (!parsed) {
    return {
      dateOfBirthLabel: '—',
      nextBirthdayLabel: '—',
      nextBirthdaySortable: null,
      daysUntil: null,
      turningAge: null,
    }
  }

  const dob = startOfDay(parsed)
  const today = startOfDay(new Date())

  let nextBirthday = setYear(dob, today.getFullYear())
  if (nextBirthday < today) {
    nextBirthday = addYears(nextBirthday, 1)
  }

  const daysUntil = differenceInCalendarDays(nextBirthday, today)
  const turningAge = differenceInYears(nextBirthday, dob)

  return {
    dateOfBirthLabel: format(dob, 'PPP'),
    nextBirthdayLabel: format(nextBirthday, 'PPP'),
    nextBirthdaySortable: nextBirthday.getTime(),
    daysUntil,
    turningAge,
  }
}

function createBirthdayRow(application: AdminApplication): BirthdayRow {
  const {
    dateOfBirthLabel,
    nextBirthdayLabel,
    nextBirthdaySortable,
    daysUntil,
    turningAge,
  } = computeBirthdayMeta(application.dateOfBirth ?? null)

  return {
    id: application.id,
    name: formatFullName(application.firstName, application.middleName, application.lastName),
    email: application.emailAddress || '—',
    phone: application.phoneNumbers || '—',
    membershipNumber: application.membershipNumber ?? null,
    category: application.categoryName || '—',
    dateOfBirth: application.dateOfBirth ?? null,
    dateOfBirthLabel,
    nextBirthdayLabel,
    nextBirthdaySortable,
    daysUntil,
    turningAge,
  }
}

function createColumns(): ColumnConfig<BirthdayRow>[] {
  return [
    {
      key: 'name',
      header: 'Member',
      accessor: (row) => row.name,
      sortable: true,
      filterable: true,
    },
    {
      key: 'membershipNumber',
      header: 'Member #',
      accessor: (row) => row.membershipNumber ?? '—',
      filterable: true,
    },
    {
      key: 'category',
      header: 'Category',
      accessor: (row) => row.category,
      sortable: true,
      filterable: true,
      cell: (value) => (
        <Badge variant="outline">
          {value as string}
        </Badge>
      ),
    },
    {
      key: 'dateOfBirth',
      header: 'Date of birth',
      accessor: (row) => row.dateOfBirth ?? '',
      sortable: true,
      cell: (_value, row) => row.dateOfBirthLabel,
    },
    {
      key: 'nextBirthday',
      header: 'Next birthday',
      accessor: (row) => row.nextBirthdaySortable ?? 0,
      sortable: true,
      cell: (_value, row) => (
        <div className="flex flex-col">
          <span>{row.nextBirthdayLabel}</span>
          <span className="text-xs text-muted-foreground">
            {row.daysUntil === 0
              ? 'Today'
              : row.daysUntil === 1
                ? 'Tomorrow'
                : row.daysUntil !== null
                  ? `In ${row.daysUntil} days`
                  : '—'}
          </span>
        </div>
      ),
    },
    {
      key: 'turningAge',
      header: 'Turning',
      accessor: (row) => row.turningAge ?? '—',
      sortable: true,
      cell: (value) => (value === null || value === '—' ? '—' : `${value as number} yrs`),
    },
    {
      key: 'contact',
      header: 'Contact',
      accessor: (row) => row.email,
      filterable: true,
      cell: (_value, row) => (
        <div className="flex flex-col">
          <span className="font-medium">{row.email}</span>
          <span className="text-xs text-muted-foreground">{row.phone}</span>
        </div>
      ),
    },
  ]
}

export function AdminBirthdaysPage() {
  const [tablePagination, setTablePagination] = useState({ pageIndex: 0, pageSize: 10 })
  const [formRange, setFormRange] = useState<DateRange>(() => buildDefaultRange())
  const [activeRange, setActiveRange] = useState<DateRange>(() => buildDefaultRange())

  const rangeError = validateRange(formRange)
  const isRangeChanged =
    formRange.from !== activeRange.from || formRange.to !== activeRange.to

  const birthdaysQuery = useListBirthdays(
    {
      page: tablePagination.pageIndex,
      size: tablePagination.pageSize,
      from: activeRange.from,
      to: activeRange.to,
    },
    {
      query: {
        placeholderData: keepPreviousData,
        select: (response) => response.data,
      },
    },
  )

  const birthdays = useMemo(
    () => mapApplicationsResponse(birthdaysQuery.data),
    [birthdaysQuery.data],
  )

  const rows: BirthdayRow[] = useMemo(
    () => birthdays.map(createBirthdayRow),
    [birthdays],
  )

  const columns = useMemo(() => createColumns(), [])

  const totalBirthdays =
    typeof birthdaysQuery.data?.totalElements === 'number'
      ? birthdaysQuery.data.totalElements
      : birthdays.length

  const isInitialLoading = birthdaysQuery.isPending && !birthdaysQuery.data
  const isError = birthdaysQuery.isError
  const hasNoData = !isInitialLoading && !isError && totalBirthdays === 0

  const quickRanges = useMemo(() => buildQuickRanges(), [])

  const applyRange = (range: DateRange) => {
    setFormRange(range)
    setActiveRange(range)
    setTablePagination((prev) => ({ ...prev, pageIndex: 0 }))
  }

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (rangeError) return
    applyRange(formRange)
  }

  if (isInitialLoading) {
    return <ApplicationListSkeleton />
  }

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <h1 className="text-2xl font-bold tracking-tight">Member birthdays</h1>
        <p className="text-sm text-muted-foreground">
          Monitor upcoming birthdays and reach out to members with timely greetings.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Date range</CardTitle>
          <CardDescription>
            {formatRangeLabel(activeRange)}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid gap-4 md:grid-cols-5 md:items-end">
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="birthday-from">From</Label>
                <Input
                  id="birthday-from"
                  type="date"
                  value={formRange.from}
                  onChange={(event) =>
                    setFormRange((prev) => ({ ...prev, from: event.target.value }))
                  }
                  max={formRange.to}
                />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="birthday-to">To</Label>
                <Input
                  id="birthday-to"
                  type="date"
                  value={formRange.to}
                  onChange={(event) =>
                    setFormRange((prev) => ({ ...prev, to: event.target.value }))
                  }
                  min={formRange.from}
                />
              </div>
              <div className="flex flex-wrap gap-2 md:justify-end">
                <Button
                  type="submit"
                  disabled={Boolean(rangeError) || !isRangeChanged}
                >
                  Apply range
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => applyRange(buildDefaultRange())}
                >
                  Reset
                </Button>
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              {quickRanges.map(({ label, range }) => (
                <Button
                  key={label}
                  type="button"
                  variant="secondary"
                  onClick={() => applyRange(range)}
                >
                  {label}
                </Button>
              ))}
            </div>

            {rangeError ? (
              <p className="text-sm text-destructive">{rangeError}</p>
            ) : null}
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
          <div>
            <CardTitle>Birthdays list</CardTitle>
            <CardDescription>
              Showing {rows.length} of {totalBirthdays} result{totalBirthdays === 1 ? '' : 's'} in this range.
            </CardDescription>
          </div>
          <div className="text-sm text-muted-foreground">
            Updated {format(new Date(), 'PPPp')}
          </div>
        </CardHeader>
        <CardContent>
          {isError ? (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Unable to load birthdays</AlertTitle>
              <AlertDescription className="flex items-center justify-between gap-2">
                <span>
                  {birthdaysQuery.error instanceof Error
                    ? birthdaysQuery.error.message
                    : 'We could not load the birthday list.'}
                </span>
                <Button variant="outline" size="sm" onClick={() => birthdaysQuery.refetch()}>
                  Try again
                </Button>
              </AlertDescription>
            </Alert>
          ) : hasNoData ? (
            <div className="flex flex-col items-center justify-center space-y-3 rounded-md border border-dashed border-muted-foreground/30 bg-muted/10 p-12 text-center">
              <AlertCircle className="h-10 w-10 text-muted-foreground" />
              <h3 className="text-lg font-semibold">No birthdays in this range</h3>
              <p className="text-sm text-muted-foreground">
                Adjust the date range to see upcoming member birthdays.
              </p>
            </div>
          ) : (
            <EnhancedDataTable
              data={rows}
              columns={columns}
              enablePagination
              enableSorting
              enableGlobalFilter
              enableExport
              enableColumnVisibility
              enableColumnFilters={false}
              searchPlaceholder="Search members…"
              loading={birthdaysQuery.isFetching && !isInitialLoading}
              emptyStateMessage="No birthdays match your filters."
              paginationCallbacks={{
                manualPagination: true,
                totalCount: totalBirthdays,
                onPageChange: (pageIndex, pageSize) =>
                  setTablePagination((prev) => {
                    if (prev.pageIndex === pageIndex && prev.pageSize === pageSize) return prev
                    return { pageIndex, pageSize }
                  }),
                onPageSizeChange: (pageSize) =>
                  setTablePagination((prev) => {
                    if (prev.pageSize === pageSize && prev.pageIndex === 0) return prev
                    return { pageIndex: 0, pageSize }
                  }),
              }}
            />
          )}
        </CardContent>
      </Card>
    </div>
  )
}
